import { Box, Card, Button, IconButton, Typography, CardContent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslate } from 'src/locales';
import { useBoolean } from 'minimal-shared/hooks';
import { Iconify } from 'src/components/iconify';
import type { IPayout } from 'src/store/slices/paymentsAPISlice';
import { PayoutHistoryDrawer } from './PayoutHistoryDrawer';

type Props = {
  currency: string;
  amount: number;
  clientId: string;
  isSelected?: boolean;
  onClick: (currency: string) => void;
  onPayoutClick: (currency: string, amount: number) => void;
  from: string;
  to: string;
  type: 'service' | 'client';
  pendingPayout: IPayout | null;
  allPayouts: IPayout[];
  onPayoutUpdated: () => void;
};

export function BalanceCard({
  currency,
  amount,
  clientId,
  isSelected = false,
  onClick,
  onPayoutClick,
  from,
  to,
  type,
  pendingPayout,
  allPayouts,
  onPayoutUpdated,
}: Props) {
  const { t } = useTranslate();
  const historyDrawer = useBoolean();

  const handleCardClick = () => {
    onClick(currency);
  };

  const handleButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (pendingPayout) {
      historyDrawer.onTrue();
    } else if (amount !== 0) {
      onPayoutClick(currency, amount);
    }
  };

  const handleHistoryClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    historyDrawer.onTrue();
  };

  const getButtonText = () => {
    if (pendingPayout) return t('common.markAsDone');
    if (amount === 0) return t('common.done');
    return type === 'client' ? t('dialogs.clientPayout.title') : t('dialogs.servicePayout.title');
  };

  const getAmountColor = () => {
    if (pendingPayout) return 'success.dark';
    if (amount === 0) return '#737A80';
    return amount < 0 ? 'error.dark' : 'text.primary';
  };

  const getButtonVariant = () => {
    if (pendingPayout) return 'contained' as const;
    if (amount === 0) return 'soft' as const;
    return 'contained' as const;
  };

  const getButtonColor = () => {
    if (pendingPayout) return 'success' as const;
    if (amount === 0) return 'inherit' as const;
    return 'primary' as const;
  };

  return (
    <>
      <Card
        onClick={handleCardClick}
        sx={(theme) => ({
          cursor: 'pointer',
          p: 1,
          backgroundColor: isSelected
            ? pendingPayout
              ? 'rgba(59, 217, 117, 0.08)'
              : amount === 0
                ? alpha('#9DA4AB', 0.24)
                : alpha('#F16437', 0.24)
            : 'white',
          border: isSelected
            ? pendingPayout
              ? '1px solid #3BD975'
              : amount === 0
                ? '1px solid #9DA4AB'
                : '1px solid #F16437'
            : '1px solid transparent',
          borderRadius: 2,
          boxShadow: `0px 4px 12px ${alpha('#9DA4AB', 0.12)}`,
          transition: theme.transitions.create(['background-color', 'border-color']),
          position: 'relative',
          '&:hover': {
            backgroundColor: isSelected
              ? pendingPayout
                ? 'rgba(59, 217, 117, 0.08)'
                : amount === 0
                  ? alpha('#9DA4AB', 0.24)
                  : alpha('#F16437', 0.24)
              : alpha('#9DA4AB', 0.08),
          },
        })}
      >
        {/* History Icon in upper right corner */}
        <IconButton
          onClick={handleHistoryClick}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'transparent',
            color: pendingPayout ? 'success.main' : getAmountColor(),
            '&:hover': {
              backgroundColor: 'transparent',
              color: pendingPayout ? 'success.dark' : getAmountColor(),
            },
          }}
        >
          <Iconify icon="lucide:history" width={16} />
        </IconButton>

        <CardContent
          sx={{
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:last-child': { pb: 2 },
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="start" gap={0}>
            <Typography variant="caption" fontWeight={400} color="text.secondary">
              {t('common.balance')}
            </Typography>
            <Typography
              variant="h5"
              component="div"
              sx={{
                color: getAmountColor(),
                fontWeight: 500,
                fontFamily: 'Inter Variable',
              }}
            >
              {amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              {currency}
            </Typography>

            <Button
              variant={getButtonVariant()}
              color={getButtonColor()}
              size="small"
              onClick={handleButtonClick}
              disabled={amount === 0 && !pendingPayout}
              sx={{
                mt: 2,
                minWidth: 150,
                fontWeight: 500,
              }}
            >
              {getButtonText()}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Payout History Drawer */}
      <PayoutHistoryDrawer
        open={historyDrawer.value}
        onClose={historyDrawer.onFalse}
        payouts={allPayouts}
        balance={`${amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${currency}`}
        from={from}
        to={to}
        hasRequestedPayout={!!pendingPayout}
        type={type}
        currency={currency}
        reference={pendingPayout?.id}
        clientId={clientId}
        onPayoutUpdated={onPayoutUpdated}
      />
    </>
  );
}
